import React from "react";
import PropTypes from "prop-types";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Popover from "@material-ui/core/Popover";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import { withStyles } from "@material-ui/styles";
import { format } from "date-fns";
import InboxOutlinedIcon from "@material-ui/icons/InboxOutlined";
import SyncIcon from "@material-ui/icons/Sync";
import businessFormStyles from "assets/jss/material-dashboard-react/components/businessForm";
import tableStyle from "assets/jss/material-dashboard-react/components/reportTables";
import SearchChips from "components/SearchComponent/SearchChips";
import SearchFields from "components/SearchComponent/SearchFields";
import NachaFilterPopover from "./NachaSearchFilter";
import { createNachaProcessingFilterPayload } from "../services/nachaService";
import { exportToCSVProvider } from "../../../../Utils/commonUtils";
import { ReactComponent as ExportIcon } from "../../../../assets/svgIcons/ExportIcon.svg";
import MenuList from "@material-ui/core/MenuList";
import Grow from "@material-ui/core/Grow";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";

const columns = [
  { id: "merchantCode", label: "Merchant Code" },
  { id: "fileName", label: "File Name" },
  { id: "accountTypeName", label: "Account Type" },
  { id: "startingTraceNumber", label: "Starting Trace Number" },
  { id: "numberOfRecords", label: "Number Of Records" },
  { id: "blockCount", label: "Block Count" },
  { id: "sftpFilePath", label: "SFTP File Path" },
  { id: "sftpSaveTime", label: "SFTP Save Time" },
  { id: "fileGenerationSuccess", label: "File Generated" },
  { id: "sftpSaveSuccess", label: "SFTP Saved" },
];

const summaryItems = [
  { key: "generatedCount", label: "Generated Files" },
  { key: "failedCount", label: "Failed Files" },
  { key: "totalHeaders", label: "Total Headers" },
  { key: "totalBatches", label: "Total Batches" },
];

const emptyFilters = {
  merchantCode: "",
  transactionHeaderId: "",
  startDate: "",
  endDate: "",
  status: "",
};

const IconsTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: "#000",
    fontSize: "12px",
  },
  tooltipPlacementTop: {
    margin: "4px 0",
  },
}))(Tooltip);

const ReasonTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: "#4a4a4a",
    fontSize: "12px",
    lineHeight: 1.45,
    maxWidth: "520px",
    padding: "10px 12px",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  arrow: {
    color: "#4a4a4a",
  },
}))(Tooltip);

const useStyles = makeStyles(theme => {
  const reportTableStyles = tableStyle(theme);
  return {
    ...businessFormStyles(theme),
    // Use shared styles from reportTables for toolbar consistency
    margin: reportTableStyles.margin,
    searchContainer: reportTableStyles.searchContainer,
    searchWrapper: reportTableStyles.searchWrapper,
    appSvgIcons: reportTableStyles.appSvgIcons,
    surface: {
      marginTop: theme.spacing(2),
      borderRadius: "6px",
      border: "1px solid #e5e5e5",
      overflow: "visible",
    },
  summaryToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 2, 1.5),
    [theme.breakpoints.down("sm")]: {
      alignItems: "stretch",
      flexDirection: "column",
    },
  },
  summaryStrip: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      rowGap: theme.spacing(1),
    },
  },
  summaryItem: {
    display: "flex",
    alignItems: "center",
    minWidth: "150px",
    padding: theme.spacing(0.5, 3, 0.5, 0.5),
    marginRight: theme.spacing(3),
    borderRight: "2px solid #d7dbe6",
    [theme.breakpoints.down("sm")]: {
      borderRight: 0,
      marginRight: 0,
      paddingRight: theme.spacing(1),
    },
  },
  summaryLabel: {
    color: "#333",
    fontSize: "15px",
    marginRight: theme.spacing(3),
    whiteSpace: "nowrap",
  },
  summaryValue: {
    color: "#111",
    fontSize: "22px",
    fontWeight: 700,
    lineHeight: 1,
  },
  popoverPaper: {
    overflow: "visible",
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[6],
    padding: 0,
    backgroundColor: theme.palette.background.paper,
  },
  filterField: {
    fontSize: "82%",
  },
  activeInputColor: {},
  dateRangeLabel: {
    color: theme.palette.primary.dark,
    display: "block",
    fontSize: "14px",
    marginBottom: theme.spacing(1),
  },
  dateField: {
    width: "100%",
    fontSize: "82%",
    "& input::-webkit-calendar-picker-indicator": {
      cursor: "pointer",
    },
  },
  dateRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: 0,
  },
  filterActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
    marginTop: "1%",
  },
  tableContainer: {
    borderTop: "1px solid #ececec",
  },
  table: {
    minWidth: 920,
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
  },
  headerCell: {
    color: "#fff",
    fontSize: "13px",
    fontWeight: 700,
    padding: theme.spacing(1.5, 2),
    whiteSpace: "nowrap",
    "& .MuiTableSortLabel-root": {
      color: "#fff",
    },
    "& .MuiTableSortLabel-root:hover": {
      color: "#fff",
    },
    "& .MuiTableSortLabel-active": {
      color: "#fff",
    },
    "& .MuiTableSortLabel-icon": {
      color: "#fff !important",
    },
  },
  bodyCell: {
    color: "#222",
    fontSize: "13px",
    padding: theme.spacing(1.5, 2),
    whiteSpace: "nowrap",
  },
  noStatusCell: {
    cursor: "pointer",
  },
  pathCell: {
    maxWidth: "360px",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  emptyCell: {
    height: "136px",
    borderBottom: "1px solid #ededed",
  },
  emptyState: {
    color: "#8f95a3",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyIcon: {
    color: "#9aa0aa",
    fontSize: "38px",
    marginBottom: theme.spacing(1),
  },
  paginationWrap: {
    borderTop: "1px solid #ededed",
  }
};
});

const getDisplayValue = (value, columnId) => {
  if (
    columnId === "fileGenerated" ||
    columnId === "fileGenerationSuccess" ||
    columnId === "sftpSaveSuccess"
  ) {
    if (value === true || String(value).toLowerCase() === "true") {
      return "Yes";
    }

    if (value === false || String(value).toLowerCase() === "false") {
      return "No";
    }

    return value === undefined || value === null || value === "" ? "-" : String(value);
  }

  if (value === true) {
    return "Yes";
  }

  if (value === false) {
    return "No";
  }

  return value === undefined || value === null || value === "" ? "-" : String(value);
};

const shouldShowReasonTooltip = (row, columnId) => (
  (columnId === "fileGenerationSuccess" || columnId === "sftpSaveSuccess") &&
  getDisplayValue(row[columnId], columnId) === "No"
);

const formatReasonTooltipTitle = reason => {
  const displayReason = getDisplayValue(reason);

  if (displayReason === "-") {
    return displayReason;
  }

  return displayReason
    .replace(/\s+(Local:)/g, "\n$1")
    .replace(/\s+(Remote:)/g, "\n$1");
};

const descendingComparator = (a, b, orderBy) => {
  const valueA = getDisplayValue(a[orderBy]).toLowerCase();
  const valueB = getDisplayValue(b[orderBy]).toLowerCase();

  if (valueB < valueA) {
    return -1;
  }

  if (valueB > valueA) {
    return 1;
  }

  return 0;
};

const getComparator = (order, orderBy) => (
  order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
);

const applyDateRange = (rowValue, startDate, endDate) => {
  if (!startDate && !endDate) {
    return true;
  }

  if (!rowValue) {
    return false;
  }

  const valueTime = new Date(rowValue).getTime();

  if (Number.isNaN(valueTime)) {
    return false;
  }

  if (startDate && valueTime < new Date(startDate).setHours(0, 0, 0, 0)) {
    return false;
  }

  if (endDate && valueTime > new Date(endDate).setHours(23, 59, 59, 999)) {
    return false;
  }

  return true;
};

function ProcessingResultTable({ details, summary, pagination, merchantOptions, onFilter }) {
  const classes = useStyles();
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("merchantCode");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [draftSearchText, setDraftSearchText] = React.useState("");
  const [appliedSearchText, setAppliedSearchText] = React.useState("");
  const [searchBar, setSearchBar] = React.useState(false);
  const [exportAnchorEl, setExportAnchorEl] = React.useState(null);
  const [draftFilters, setDraftFilters] = React.useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = React.useState(emptyFilters);
  const initialFilterCalled = React.useRef(false);
  const hasActiveCriteria = Boolean(draftSearchText.trim()) || Object.values(draftFilters).some(value => value !== "");
  const exportPopoverOpen = Boolean(exportAnchorEl);

  const parseDateValue = value => {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
  };

  const startDateValue = parseDateValue(draftFilters.startDate);
  const endDateValue = parseDateValue(draftFilters.endDate);
  const dateRangeError =
    startDateValue && endDateValue && startDateValue > endDateValue
      ? "Start Date must be before or equal to End Date."
      : "";

  const sortedRows = React.useMemo(() => (
    [...details].sort(getComparator(order, orderBy))
  ), [details, order, orderBy]);

  const visibleRows = React.useMemo(() => {
    // If backend provides pagination, `details` contains only server-returned rows
    // and we must render exactly those rows without additional client-side slicing.
    if (pagination && Number.isFinite(Number(pagination.totalRows))) {
      return sortedRows;
    }

    // Fallback to client-side paging when backend pagination is not available.
    return sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedRows, page, rowsPerPage, pagination, details.length]);

  // Keep local pagination state in sync with backend response when available.
  React.useEffect(() => {
    if (pagination && Number.isFinite(Number(pagination.pageNumber))) {
      const nextPage = Math.max(0, Number(pagination.pageNumber) - 1);
      setPage(nextPage);
    }

    if (pagination && Number.isFinite(Number(pagination.pageSize))) {
      setRowsPerPage(Number(pagination.pageSize));
    }
  }, [pagination]);

  // When backend pagination is not used, reset page to 0 on details change.
  React.useEffect(() => {
    if (!pagination) {
      setPage(0);
    }
  }, [details, pagination]);

  const handleSort = columnId => {
    const isAsc = orderBy === columnId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnId);
  };

  const handleFilterChange = field => event => {
    setDraftFilters(prevFilters => ({
      ...prevFilters,
      [field]: event.target.value,
    }));
  };

  const formatDateValue = date => {
    if (!date) {
      return "";
    }

    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      return format(date, "yyyy-MM-dd");
    }

    return "";
  };

  const handleDateChange = field => date => {
    setDraftFilters(prevFilters => ({
      ...prevFilters,
      [field]: formatDateValue(date),
    }));
  };

  const handleSearchOpen = () => {
    setSearchBar(true);
  };

  const handleSearchClose = () => {
    setSearchBar(false);
  };

  const handleToggleExport = event => {
    setExportAnchorEl(prev => (prev ? null : event.currentTarget));
  };

  const handleCloseExport = () => {
    setExportAnchorEl(null);
  };

  const handleRefresh = () => {
    if (typeof onFilter === "function") {
      onFilter(createNachaProcessingFilterPayload({
        ...appliedFilters,
        searchText: appliedSearchText.trim(),
        pageNumber: page + 1,
        pageSize: rowsPerPage,
      }));
    }
  };

  const handleApplyFilters = event => {
    if (event) {
      event.preventDefault();
    }

    if (!hasActiveCriteria || dateRangeError) {
      return;
    }

    setAppliedFilters(draftFilters);
    setAppliedSearchText(draftSearchText.trim());
    setPage(0);
    setSearchBar(false);

    if (typeof onFilter === "function") {
      onFilter(createNachaProcessingFilterPayload({
        ...draftFilters,
        searchText: draftSearchText.trim(),
        pageNumber: 1,
        pageSize: rowsPerPage,
      }));
    }
  };

  const handleClearFilters = () => {
    if (!hasActiveCriteria) {
      return;
    }

    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setDraftSearchText("");
    setAppliedSearchText("");
    setPage(0);
    setSearchBar(false);

    if (typeof onFilter === "function") {
      onFilter(createNachaProcessingFilterPayload({
        pageNumber: 1,
        pageSize: rowsPerPage,
      }));
    }
  };

  React.useEffect(() => {
    if (initialFilterCalled.current) {
      return;
    }

    initialFilterCalled.current = true;

    if (typeof onFilter === "function") {
      onFilter(createNachaProcessingFilterPayload({
        pageNumber: 1,
        pageSize: rowsPerPage,
      }));
    }
  }, [onFilter, rowsPerPage]);

  const getChipLabel = () => {
    const chips = [];

    if (appliedSearchText.trim()) {
      chips.push(`Search: ${appliedSearchText.trim()}`);
    }

    if (appliedFilters.merchantCode) {
      chips.push(`Merchant Code: ${appliedFilters.merchantCode}`);
    }

    if (appliedFilters.transactionHeaderId) {
      chips.push(`Transaction Header Id: ${appliedFilters.transactionHeaderId}`);
    }

    if (appliedFilters.status) {
      chips.push(`Status: ${appliedFilters.status}`);
    }

    if (appliedFilters.startDate || appliedFilters.endDate) {
      const start = appliedFilters.startDate || "Any";
      const end = appliedFilters.endDate || "Any";
      chips.push(`Date: ${start} - ${end}`);
    }

    return chips;
  };

  const handleDeleteChip = chip => {
    const nextDraftFilters = { ...draftFilters };
    const nextAppliedFilters = { ...appliedFilters };
    let nextSearchText = appliedSearchText;

    if (chip.startsWith("Search: ")) {
      nextSearchText = "";
    } else if (chip.startsWith("Merchant Code: ")) {
      nextDraftFilters.merchantCode = "";
      nextAppliedFilters.merchantCode = "";
    } else if (chip.startsWith("Transaction Header Id: ")) {
      nextDraftFilters.transactionHeaderId = "";
      nextAppliedFilters.transactionHeaderId = "";
    } else if (chip.startsWith("Status: ")) {
      nextDraftFilters.status = "";
      nextAppliedFilters.status = "";
    } else if (chip.startsWith("Date: ")) {
      nextDraftFilters.startDate = "";
      nextDraftFilters.endDate = "";
      nextAppliedFilters.startDate = "";
      nextAppliedFilters.endDate = "";
    }

    setDraftFilters(nextDraftFilters);
    setAppliedFilters(nextAppliedFilters);
    setDraftSearchText(nextSearchText);
    setAppliedSearchText(nextSearchText);

    if (typeof onFilter === "function") {
      onFilter(createNachaProcessingFilterPayload({
        ...nextAppliedFilters,
        searchText: nextSearchText.trim(),
        pageNumber: 1,
        pageSize: rowsPerPage,
      }));
    }
  };

  const chips = React.useMemo(getChipLabel, [appliedFilters, appliedSearchText]);

  const exportNachaData = () => {
    if (!details || details.length === 0) {
      return;
    }

    const mappedLogs = details.map(row => columns.reduce((mappedRow, column) => ({
      ...mappedRow,
      [column.label]: getDisplayValue(row[column.id], column.id),
    }), {}));

    exportToCSVProvider(
      mappedLogs,
      "NACHA Processing",
      chips.join(", "),
      "",
      "",
      ""
    );
    handleCloseExport();
  };

  return (
    <Paper className={classes.surface}>
      <div className={classes.summaryToolbar}>
        <div className={classes.summaryStrip}>
          {summaryItems.map(item => (
            <div className={classes.summaryItem} key={item.key}>
              <Typography className={classes.summaryLabel}>{item.label}</Typography>
              <Typography className={classes.summaryValue}>{summary[item.key] || 0}</Typography>
            </div>
          ))}
        </div>

        <Grid container className={classes.searchContainer}>
          <Grid item className={classes.margin}>
            <div className={classes.searchWrapper}>
              <SearchFields
                searchBar={searchBar}
                handleSearchClose={handleSearchClose}
                handleSearchOpen={handleSearchOpen}
                searchValue={draftSearchText}
                handleSearchChange={event => setDraftSearchText(event.target.value)}
                placeholder="Search"
                Fields={() => (
                  <NachaFilterPopover
                    values={draftFilters}
                    searchText={draftSearchText}
                    merchantOptions={merchantOptions}
                    onChange={field => event => handleFilterChange(field)(event)}
                    onDateChange={field => date => handleDateChange(field)(date)}
                    onSearch={handleApplyFilters}
                    onClear={handleClearFilters}
                    startDateValue={startDateValue}
                    endDateValue={endDateValue}
                    dateRangeError={dateRangeError}
                  />
                )}
              />
              <IconsTooltip title="Refresh" placement="top">
                <div className={classes.appSvgIcons}>
                  <SyncIcon
                    style={{ marginRight: "10px" }}
                    fontSize="large"
                    color="primary"
                    onClick={handleRefresh}
                  />
                </div>
              </IconsTooltip>
              <IconsTooltip title="Export" placement="top">
                <div className={classes.appSvgIcons}>
                  <ExportIcon
                    onClick={handleToggleExport}
                  />
                </div>
              </IconsTooltip>
              <Popover
                open={exportPopoverOpen}
                anchorEl={exportAnchorEl}
                onClose={handleCloseExport}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                disableRestoreFocus
                PaperProps={{ className: classes.popoverPaper }}
              >
                <ClickAwayListener onClickAway={handleCloseExport}>
                  <Grow in={exportPopoverOpen} style={{ transformOrigin: "left top" }}>
                    <Paper>
                      <MenuList>
                        <MenuItem onClick={exportNachaData}>Export to Excel</MenuItem>
                      </MenuList>
                    </Paper>
                  </Grow>
                </ClickAwayListener>
              </Popover>
            </div>
          </Grid>
        </Grid>
      </div>
      {chips.length > 0 && (
        <SearchChips
          searchValues={chips}
          handleDeleteChip={handleDeleteChip}
        />
      )}

      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow className={classes.tableHeader}>
              {columns.map(column => (
                <TableCell className={classes.headerCell} key={column.id} sortDirection={orderBy === column.id ? order : false}>
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length > 0 ? (
              visibleRows.map(row => (
                <TableRow hover key={row.id}>
                  {columns.map(column => (
                    <TableCell
                      className={`${classes.bodyCell} ${column.id === "sftpFilePath" ? classes.pathCell : ""}`}
                      key={column.id}
                    >
                      {shouldShowReasonTooltip(row, column.id) ? (
                        <ReasonTooltip
                          arrow
                          placement="top"
                          title={<span style={{ whiteSpace: "pre-line" }}>{formatReasonTooltipTitle(row.reason)}</span>}
                        >
                          <span className={classes.noStatusCell}>{getDisplayValue(row[column.id], column.id)}</span>
                        </ReasonTooltip>
                      ) : (
                        getDisplayValue(row[column.id], column.id)
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell className={classes.emptyCell} colSpan={columns.length}>
                  <Box className={classes.emptyState}>
                    <InboxOutlinedIcon className={classes.emptyIcon} />
                    <Typography variant="body2">No data available</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div className={classes.paginationWrap}>
        <TablePagination
          component="div"
          count={pagination?.totalRows || details.length}
          onChangePage={(event, nextPage) => {
            setPage(nextPage);
            if (typeof onFilter === "function") {
              onFilter(createNachaProcessingFilterPayload({
                ...appliedFilters,
                searchText: appliedSearchText.trim(),
                pageNumber: nextPage + 1,
                pageSize: rowsPerPage,
              }));
            }
          }}
          onChangeRowsPerPage={event => {
            const nextSize = parseInt(event.target.value, 10);
            setRowsPerPage(nextSize);
            setPage(0);

            if (typeof onFilter === "function") {
              onFilter(createNachaProcessingFilterPayload({
                ...appliedFilters,
                searchText: appliedSearchText.trim(),
                pageNumber: 1,
                pageSize: nextSize,
              }));
            }
          }}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </div>
    </Paper>
  );
}

ProcessingResultTable.propTypes = {
  details: PropTypes.arrayOf(PropTypes.shape({
    accountTypeName: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    blockCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    error: PropTypes.string,
    failureReason: PropTypes.string,
    fileGenerated: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    fileGenerationSuccess: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    fileName: PropTypes.string,
    merchantCode: PropTypes.string,
    numberOfRecords: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    reason: PropTypes.string,
    sftpFilePath: PropTypes.string,
    sftpSaveTime: PropTypes.string,
    sftpSaveSuccess: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    startingTraceNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    transactionHeaderId: PropTypes.string,
  })),
  summary: PropTypes.shape({
    generatedCount: PropTypes.number,
    failedCount: PropTypes.number,
    totalHeaders: PropTypes.number,
    totalBatches: PropTypes.number,
  }),
  merchantOptions: PropTypes.arrayOf(PropTypes.string),
  onFilter: PropTypes.func,
  pagination: PropTypes.shape({
    pageNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pageSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    totalRows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

ProcessingResultTable.defaultProps = {
  details: [],
  summary: {
    generatedCount: 0,
    failedCount: 0,
    totalHeaders: 0,
    totalBatches: 0,
  },
  merchantOptions: [],
};

export default ProcessingResultTable;














